import React, { useState, useCallback, useContext } from "react";
import PropTypes from "prop-types";
import PerfectScrollbar from "react-perfect-scrollbar";
import makeStyles from "@mui/styles/makeStyles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import ProgressIndicatorOverlay from "components/ProgressIndicatorOverlay";
import { dateTimeFormat } from "core/datetime";
import ReportForm from "components/CaseManagement/CaseForms/ReportForm";
import EventModelState from "components/CaseManagement/State/EventModelState";
import styles from "assets/jss/default/components/newCaseWithDiagnosticDialogStyle.js";
import { CaseContext } from "contexts/CaseContext";
import { isDraftJsState } from "core/utils/json";
import { CaseEventType } from "interfaces/case-event";
import { AxiosContext } from "contexts/AxiosContext";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { useMutation } from "@tanstack/react-query";
import { usePostEvent } from "hooks/cases/usePostEvent";

const useStyles = makeStyles(styles);

export default function NewReportDialog(props) {
  const { open, setOpen, reportVariant, currentCase, refetchStream } = props;
  const classes = useStyles();
  const { axios } = useContext(AxiosContext);

  const { refetchCase } = useContext(CaseContext);

  console.info(reportVariant);

  const dialogTypeString =
    reportVariant === "Diagnosis"
      ? "Diagnostic"
      : reportVariant.charAt(0).toUpperCase() + reportVariant.slice(1);
  const dialogTitle = `New ${dialogTypeString} Report`;

  const [eventModel, setEventModel] = useState(EventModelState.createEmpty());
  const [attachmentsToUpload, setAttachmentsToUpload] = useState([]);
  const [filesDropping, setFilesDropping] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [selectedStatusId, setSelectedStatusId] = useState();
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);

  const closeDialog = () => {
    setEventModel(EventModelState.createEmpty());
    setSelectedStatusId();
    setOpen(false);
  };

  const handleAxiosError = defaultErrorMessage => err => {
    addSnackbarToQueue({
      severity: "error",
      message: err?.response?.data?.error || defaultErrorMessage
    });
  };

  const postCreateEvent = async newReport => {
    const { data } = await axios.post("events", newReport);

    return data;
  };

  const usePostEventAttachment = useMutation(["post-event-attachment"], {
    mutationFn: ({ eventId, file }) => {
      const formData = new FormData();
      formData.append("file", file);
      return axios
        .post(`events/${eventId}/attachments`, formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        })
        .then(r => r);
    },
    onError: handleAxiosError("Error uploading file.")
  });

  const onReportFormChange = useCallback(
    ({ eventModel: updatedEventModel, severityStatus, isValid }) => {
      setSelectedStatusId(severityStatus);
      setEventModel(EventModelState.set(eventModel, updatedEventModel));
      setIsFormValid(isValid);
    },
    [eventModel]
  );

  const getReportType = reportVariant => {
    switch (reportVariant) {
      case "Diagnosis":
        return CaseEventType.DIAGNOSTIC;

      case "Repair":
        return CaseEventType.REPAIR;

      case "Inspection":
        return CaseEventType.INSPECTION;

      default:
        console.info("Unknown report type");
        break;
    }
  };

  const createNewReport = async () => {
    const event = eventModel.getImmutable();

    const createReport = {
      date_time: dateTimeFormat(event.date_time, { timeZone: "utc" }),
      type: getReportType(reportVariant),
      description: isDraftJsState(event.description)
        ? JSON.stringify(event.description)
        : event.description,
      case: currentCase.id,
      case_severity_type: selectedStatusId,
      recommendation: event.recommendation,
      params: {
        feedComment: []
      }
    };

    let eventId;
    try {
      eventId = (await postCreateEvent(createReport)).id;
    } catch (error) {
      console.error(error);
    }

    try {
      for (const attachment of attachmentsToUpload) {
        await usePostEventAttachment.mutateAsync({
          eventId,
          file: attachment.contents
        });
      }
    } catch (err) {
      console.error(err);
      return;
    }

    await Promise.all([refetchStream(), refetchCase()]);
    closeDialog();
  };

  const isLoading =
    usePostEvent.isLoading ||
    usePostEventAttachment.isLoading ||
    usePostEvent.isMutating ||
    usePostEventAttachment.isMutating;

  return (
    <Dialog
      open={open}
      fullWidth={true}
      maxWidth="sm"
      onClose={() => setOpen(false)}
      aria-labelledby="new-report-dialog"
    >
      <ProgressIndicatorOverlay loading={isLoading} />
      <DialogTitle className={classes.title}>
        <Grid container direction="row" alignItems="center">
          <Grid item xs={12} md={12}>
            {dialogTitle}
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent>
        <PerfectScrollbar>
          <ReportForm
            key={dialogTitle}
            onFormChange={onReportFormChange}
            variant={reportVariant?.toLowerCase() || "diagnosis"}
            eventModel={eventModel.toJS()}
            severityStatus={selectedStatusId}
            attachmentsToUpload={attachmentsToUpload}
            setAttachmentsToUpload={setAttachmentsToUpload}
            filesDropping={filesDropping}
            setFilesDropping={setFilesDropping}
          />
        </PerfectScrollbar>
      </DialogContent>
      <DialogActions className={classes.buttonArea}>
        <Button
          className={classes.cancelButton}
          onClick={() => closeDialog()}
          variant="outlined"
          color="primary"
          data-qa="CP-Button-Cancel-Report"
        >
          Cancel
        </Button>
        <Button
          className={classes.completeButton}
          disabled={isLoading || !isFormValid || filesDropping}
          onClick={() => createNewReport()}
          variant="contained"
          color="primary"
          data-qa="CP-Button-Complete-Report"
        >
          Complete
        </Button>
      </DialogActions>
    </Dialog>
  );
}

NewReportDialog.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  reportVariant: PropTypes.string,
  currentCase: PropTypes.object,
  refetchStream: PropTypes.func
};
